import * as React from 'react'

import throttle from 'lodash/throttle'
import {
  GeoJSONFeature,
  Map as MapboxMap,
  MapMouseEvent,
  PointLike,
} from 'mapbox-gl'
import { MapRef } from 'react-map-gl/mapbox'

type HoverFeature = GeoJSONFeature

interface IMapHoverProbeOptions {
  // False unbinds the probe entirely (no hoverable targets / layer hidden).
  enabled: boolean
  // Hit-tested per settle; layerIds[0] also gates on existence so the query
  // never runs before the style has added the layers.
  layerIds: string[]
  // Hit-tested only when layerIds miss: captions that draw above what they name.
  fallbackLayerIds?: string[]
  throttleMs: number
  // Topmost hit feature, or null when the pointer leaves the targets. Reports only: the caller
  // turns a hit into a pointer through MapBoxViewer's `cursor`, the canvas cursor's one writer.
  onHit?: (feature: HoverFeature | null) => void
}

// queryRenderedFeatures returns hits in style order whatever order `layers` lists them in, so a
// priority between layers takes one query per pass. `accept` skips a hit that resolves to nothing,
// so it cannot shadow a live one under it.
export function queryFirstHit(
  map: Pick<MapboxMap, 'getLayer' | 'queryRenderedFeatures'>,
  point: PointLike,
  passes: string[][],
  accept: (hit: HoverFeature) => boolean = () => true,
): HoverFeature | null {
  for (const layerIds of passes) {
    // Only the layers actually on the map. queryRenderedFeatures THROWS on an id that is not in
    // the style, so one layer failing to mount would otherwise cost every other layer its hit.
    const present = layerIds.filter(id => map.getLayer(id))
    if (!present.length) continue
    const hit = map
      .queryRenderedFeatures(point, { layers: present })
      .find(accept)
    if (hit) return hit
  }
  return null
}

// Throttled mousemove probe that sits out camera motion. NOT delegated
// mouseenter/mouseleave: mapbox hit-tests those on EVERY mousemove — drag-pan
// frames included — which stalls camera interaction.
export function useMapHoverProbe(
  map: MapRef | undefined,
  {
    enabled,
    layerIds,
    fallbackLayerIds,
    throttleMs,
    onHit,
  }: IMapHoverProbeOptions,
): void {
  const onHitRef = React.useRef(onHit)
  onHitRef.current = onHit
  // Stable dep so the effect rebinds on a layer-set change, not every render.
  const layersKey = `${layerIds.join(',')}|${fallbackLayerIds?.join(',')}`

  React.useEffect(() => {
    if (!map || !enabled) return undefined
    const guardLayerId = layerIds[0]
    const probe = throttle(
      (e: MapMouseEvent): void => {
        if (map.isMoving() || !map.getLayer(guardLayerId)) return
        onHitRef.current?.(
          queryFirstHit(map, e.point, [layerIds, fallbackLayerIds ?? []]),
        )
      },
      throttleMs,
      { leading: true, trailing: true },
    )
    const clear = (): void => {
      probe.cancel()
      onHitRef.current?.(null)
    }
    map.on('mousemove', probe)
    map.on('mouseout', clear)
    return () => {
      map.off('mousemove', probe)
      map.off('mouseout', clear)
      clear()
    }
  }, [map, enabled, layersKey, throttleMs])
}
